/**
 * Pure helpers for building one Plotly fan chart per macro variable.
 *
 * No React or Plotly imports — just data transforms and `Data`/`Layout`
 * builders that `<PlotlyChart>` can consume directly.
 */
import type { Annotations, Data, Layout, Shape } from 'plotly.js';
import { hexToRgba } from '@/lib/plotly/colors';
import { hoverTemplate } from '@/lib/plotly/hoverTemplates';
import { chartLayout } from '@/lib/plotly/layoutPresets';
import type { FanChartDef, FanRecord, ProcessedFanData, YRange } from '@/features/mars/live/types';
import type { InflationRow } from '@/features/mars/forecasts/types';

/** Fan charts in the grid. Do not reorder — the grid layout depends on it. */
export const FAN_CHARTS: readonly FanChartDef[] = [
  {
    variable: 'GDP',
    dataType: 'gdp',
    title: 'US Real GDP Growth (%YoY)',
    yFormat: '.1f',
    colorScheme: ['#d62728', '#ffcccb', '#ff6b6b'],
  },
  {
    variable: 'Inflation',
    dataType: 'inflation',
    title: 'US CPI Inflation Rate (%YoY)',
    yFormat: '.1f',
    colorScheme: ['#d62728', '#ffcccb', '#ff6b6b'],
  },
  {
    variable: 'T-Bill',
    dataType: 'tbill',
    title: 'US 3-Month T-Bill Rate (%)',
    yFormat: '.1f',
    colorScheme: ['#d62728', '#ffcccb', '#ff6b6b'],
  },
  {
    // Stored as MoM annualized growth in the DB; the backend converts to
    // YoY on read (see backend MarsVintageLogic), hence the growth title.
    variable: 'Dividend Yield',
    dataType: 'dividend_yield',
    title: 'Real Dividend Growth (%YoY)',
    yFormat: '.1f',
    colorScheme: ['#2ca02c', '#c7e9c0', '#74c476'],
  },
  {
    variable: '10 Year',
    dataType: 'ten_year',
    title: '10 Year Yield (%)',
    yFormat: '.1f',
    colorScheme: ['#1f77b4', '#c6dbef', '#6baed6'],
  },
  {
    variable: 'BAA Spread',
    dataType: 'baa_spread',
    title: 'BAA Spread (%)',
    yFormat: '.1f',
    colorScheme: ['#ff7f0e', '#fdd0a2', '#fdae6b'],
  },
];

/**
 * Normalize raw API rows into aligned parallel arrays. Picks `p16/p84` when
 * present, falling back to the older `p25/p75` column names.
 */
export function processVintageData(raw: FanRecord[]): ProcessedFanData {
  const sorted = [...raw].sort(
    (a, b) => new Date(a.obs_date).getTime() - new Date(b.obs_date).getTime(),
  );

  const out: ProcessedFanData = {
    dates: [],
    p5: [],
    p16: [],
    p50: [],
    p84: [],
    p95: [],
    actual: [],
    previous_month: [],
  };

  for (const r of sorted) {
    if (!r.obs_date) continue;
    out.dates.push(r.obs_date);
    out.p5.push(r.p5 ?? null);
    out.p16.push(r.p16 ?? r.p25 ?? null);
    out.p50.push(r.p50 ?? null);
    out.p84.push(r.p84 ?? r.p75 ?? null);
    out.p95.push(r.p95 ?? null);
    if (r.is_actual) {
      out.actual.push(r.p50 ?? null);
    } else {
      out.actual.push(null);
    }
    out.previous_month.push(r.previous_month ?? null);
  }

  return out;
}

/**
 * Read the first finite numeric value from `row` across a list of candidate
 * column names. Tolerates the loosely-typed inflation payload where a
 * percentile column may arrive as a number, a numeric string, `null`,
 * `undefined`, or `''`, in any of several casings.
 */
function readNumber(row: InflationRow, ...keys: string[]): number | null {
  for (const k of keys) {
    const v = row[k];
    if (v === null || v === undefined || v === '') continue;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * Normalize raw inflation API rows into the shared `ProcessedFanData` shape.
 *
 * Same percentile/actual columns as `processVintageData`, plus the two
 * inflation-only series: `one_month` (previous-month forecast, → `oneMonth`)
 * and `cb` (central-bank forecast markers, → `cb`). Column names may arrive in
 * any casing, so each is resolved through `readNumber`.
 */
export function processInflationRows(rows: InflationRow[]): ProcessedFanData {
  const sorted = [...rows].sort((a, b) => {
    const da = new Date(String(a.date ?? a.obs_date ?? ''));
    const db = new Date(String(b.date ?? b.obs_date ?? ''));
    return da.getTime() - db.getTime();
  });

  return {
    dates: sorted.map((r) => String(r.date ?? r.obs_date ?? '')),
    p5: sorted.map((r) => readNumber(r, 'p5', 'P5')),
    p16: sorted.map((r) => readNumber(r, 'p16', 'P16')),
    p50: sorted.map((r) => readNumber(r, 'p50', 'P50')),
    p84: sorted.map((r) => readNumber(r, 'p84', 'P84')),
    p95: sorted.map((r) => readNumber(r, 'p95', 'P95')),
    actual: sorted.map((r) => readNumber(r, 'actual', 'Actual')),
    previous_month: sorted.map(() => null),
    oneMonth: sorted.map((r) => readNumber(r, 'one_month', 'One_Month', 'OneMonth')),
    cb: sorted.map((r) => readNumber(r, 'cb', 'CB', 'central_bank')),
  };
}

/**
 * Merge `p50` from the previous-month vintage into `previous_month` for
 * forecast rows.
 */
export function mergePreviousMonth(current: FanRecord[], prev: FanRecord[] | null): FanRecord[] {
  if (!prev) return current;
  const lookup = new Map<string, number>();
  for (const r of prev) {
    if (!r.is_actual && r.p50 != null) lookup.set(r.obs_date, r.p50);
  }
  return current.map((r) => {
    if (r.is_actual) return r;
    const prevVal = lookup.get(r.obs_date);
    return { ...r, previous_month: prevVal ?? r.previous_month ?? null };
  });
}

function sliceForecast(p: ProcessedFanData): {
  dates: string[];
  p5: Array<number | null>;
  p16: Array<number | null>;
  p50: Array<number | null>;
  p84: Array<number | null>;
  p95: Array<number | null>;
} {
  const firstForecast = p.actual.findIndex((v, i) => v === null && p.p50[i] !== null);
  if (firstForecast < 0) {
    return { dates: p.dates, p5: p.p5, p16: p.p16, p50: p.p50, p84: p.p84, p95: p.p95 };
  }
  return {
    dates: p.dates.slice(firstForecast),
    p5: p.p5.slice(firstForecast),
    p16: p.p16.slice(firstForecast),
    p50: p.p50.slice(firstForecast),
    p84: p.p84.slice(firstForecast),
    p95: p.p95.slice(firstForecast),
  };
}

/**
 * Build the Plotly `Data[]` for one fan chart.
 *
 * Trace order — do not reorder (legend + z-order depend on it):
 *   0. p5 floor (band, invisible)
 *   1. p95 (outer band fill, invisible stroke)
 *   2. p16 floor (band, invisible)
 *   3. p84 (inner band fill, invisible stroke)
 *   4. "One Month Ago" — gray dashed, width 2 (only if prev-month data)
 *   5. "Fulcrum" — accent-colored solid median, width 3 (always)
 *   6. "Actual" — black solid, width 2 (only if actuals exist), extended to
 *      the first forecast point for a smooth handoff
 *
 * When `opts.variant` is `'inflation'`, the builder instead emits the
 * inflation-tab rendering: full-width (un-sliced) bands with fixed pink
 * fills, a black "Historical" line, a red "Forecast" line spliced to the
 * last historical point, an optional gray-dashed "One Month Ago Forecast"
 * line, and optional blue "Central Bank Forecast" markers. See
 * `buildInflationFanTraces`. In that mode `def` is unused (the inflation
 * rendering derives all colours/widths/naming internally) and may be
 * omitted.
 */
export function buildFanTraces(
  processed: ProcessedFanData,
  def: FanChartDef | undefined,
  opts: { fanAlpha?: number; variant?: 'macro' | 'inflation' } = {},
): Data[] {
  if (opts.variant === 'inflation') {
    return buildInflationFanTraces(processed);
  }
  if (!def) {
    throw new Error('buildFanTraces: `def` is required for the macro variant');
  }
  const { fanAlpha = 0.6 } = opts;
  const forecast = sliceForecast(processed);
  const [middleColor, outerColor, innerColor] = def.colorScheme;

  // A single fanAlpha drives two band opacities:
  // outer = alpha * 0.6 (lighter), inner = alpha (darker).
  const outerAlpha = fanAlpha * 0.6;
  const innerAlpha = fanAlpha;
  const rgbaOuter = hexToRgba(outerColor, outerAlpha);
  const rgbaInner = hexToRgba(innerColor, innerAlpha);

  const traces: Data[] = [];

  // p5 floor — invisible line for the outer band. Suppress the legend entry.
  traces.push({
    x: forecast.dates,
    y: forecast.p5,
    type: 'scatter',
    mode: 'lines',
    line: { color: 'rgba(0,0,0,0)', width: 0 },
    showlegend: false,
    hoverinfo: 'skip',
    name: 'p5',
  });
  // p95 — filled to the previous trace.
  traces.push({
    x: forecast.dates,
    y: forecast.p95,
    type: 'scatter',
    mode: 'lines',
    line: { color: 'rgba(0,0,0,0)', width: 0 },
    fill: 'tonexty',
    fillcolor: rgbaOuter,
    showlegend: false,
    hoverinfo: 'skip',
    name: 'p95',
  });
  // p16 floor — invisible line for the inner band.
  traces.push({
    x: forecast.dates,
    y: forecast.p16,
    type: 'scatter',
    mode: 'lines',
    line: { color: 'rgba(0,0,0,0)', width: 0 },
    showlegend: false,
    hoverinfo: 'skip',
    name: 'p16',
  });
  // p84 — filled to the previous trace.
  traces.push({
    x: forecast.dates,
    y: forecast.p84,
    type: 'scatter',
    mode: 'lines',
    line: { color: 'rgba(0,0,0,0)', width: 0 },
    fill: 'tonexty',
    fillcolor: rgbaInner,
    showlegend: false,
    hoverinfo: 'skip',
    name: 'p84',
  });

  // Previous month overlay — drawn first among the foreground traces.
  const hasPrev = processed.previous_month.some((v) => v != null);
  if (hasPrev) {
    const px: string[] = [];
    const py: Array<number | null> = [];
    processed.dates.forEach((date, i) => {
      if (processed.previous_month[i] != null) {
        px.push(date);
        py.push(processed.previous_month[i]);
      }
    });
    if (px.length) {
      traces.push({
        x: px,
        y: py,
        type: 'scatter',
        mode: 'lines',
        line: { color: '#666666', width: 2, dash: 'dash' },
        showlegend: true,
        name: 'One Month Ago',
        hovertemplate: '%{fullData.name}: %{y:.1f}<extra></extra>',
      });
    }
  }

  // Forecast median trace; named "Fulcrum" (user-facing label).
  traces.push({
    x: forecast.dates,
    y: forecast.p50,
    type: 'scatter',
    mode: 'lines',
    line: { color: middleColor, width: 3 },
    showlegend: true,
    name: 'Fulcrum',
    hovertemplate: '%{fullData.name}: %{y:.1f}<extra></extra>',
  });

  // Actuals — extended to the first forecast point.
  const hasActual = processed.actual.some((v) => v !== null);
  if (hasActual) {
    const ax: string[] = [];
    const ay: Array<number | null> = [];
    let firstForecastIdx = -1;
    processed.dates.forEach((date, i) => {
      if (processed.actual[i] !== null) {
        ax.push(date);
        ay.push(processed.actual[i]);
      } else if (firstForecastIdx < 0 && processed.p50[i] !== null) {
        firstForecastIdx = i;
        ax.push(date);
        ay.push(processed.p50[i]);
      }
    });
    if (ax.length) {
      traces.push({
        x: ax,
        y: ay,
        type: 'scatter',
        mode: 'lines',
        line: { color: '#000000', width: 2 },
        showlegend: true,
        name: 'Actual',
        hovertemplate: '%{fullData.name}: %{y:.1f}<extra></extra>',
      });
    }
  }

  return traces;
}

/**
 * Build the Plotly `Data[]` for the Inflation Forecasts tab fan chart.
 *
 * This is a deliberately separate rendering from the macro `buildFanTraces`
 * path — the inflation chart is parity-sensitive and diverges in trace
 * z-order, fill direction, band colours, line widths, and the historical→
 * forecast splice. Reached via `buildFanTraces(processed, def, { variant:
 * 'inflation' })`.
 *
 * Trace order — do not reorder (legend + z-order depend on it):
 *   0. p95 (90% band, invisible stroke, no fill)
 *   1. p5 (90% band, invisible stroke, `tonexty` fill — pink, light)
 *   2. p84 (68% band, invisible stroke, no fill)
 *   3. p16 (68% band, invisible stroke, `tonexty` fill — pink, dark)
 *   4. "Historical" — black solid, width 2.5 (only if actuals exist)
 *   5. "Forecast" — red solid, width 2.5, spliced to the last historical
 *      point for a continuous handoff (only if forecast points exist)
 *   6. "One Month Ago Forecast" — gray dashed, width 2 (only if data)
 *   7. "Central Bank Forecast" — blue circle markers (only if data)
 *
 * Unlike the macro path the bands are NOT sliced to the forecast window;
 * they span the full date axis, and the fill is drawn upper-then-lower
 * (p95 first, p5 with `tonexty`) rather than floor-then-ceiling.
 */
function buildInflationFanTraces(processed: ProcessedFanData): Data[] {
  const oneMonth = processed.oneMonth ?? [];
  const cb = processed.cb ?? [];
  const traces: Data[] = [];

  // 90% outer band (wider, lighter): upper first, then lower with tonexty to fill.
  traces.push({
    type: 'scatter',
    mode: 'lines',
    name: '90% Confidence',
    x: processed.dates,
    y: processed.p95,
    line: { width: 0, color: 'rgba(252, 165, 165, 0)' },
    showlegend: false,
    hoverinfo: 'skip',
  });
  traces.push({
    type: 'scatter',
    mode: 'lines',
    name: '90% Confidence',
    x: processed.dates,
    y: processed.p5,
    line: { width: 0, color: 'rgba(252, 165, 165, 0)' },
    fill: 'tonexty',
    fillcolor: 'rgba(252, 165, 165, 0.5)',
    hoverinfo: 'skip',
  });

  // 68% inner band.
  traces.push({
    type: 'scatter',
    mode: 'lines',
    name: '68% Confidence',
    x: processed.dates,
    y: processed.p84,
    line: { width: 0, color: 'rgba(254, 226, 226, 0)' },
    showlegend: false,
    hoverinfo: 'skip',
  });
  traces.push({
    type: 'scatter',
    mode: 'lines',
    name: '68% Confidence',
    x: processed.dates,
    y: processed.p16,
    line: { width: 0, color: 'rgba(254, 226, 226, 0)' },
    fill: 'tonexty',
    fillcolor: 'rgba(254, 226, 226, 0.8)',
    hoverinfo: 'skip',
  });

  // Historical (black) and Forecast (red) traces built from the paired
  // (actual, p50) arrays for continuity at the splice point.
  const historicalX: string[] = [];
  const historicalY: number[] = [];
  const forecastX: string[] = [];
  const forecastY: number[] = [];

  processed.dates.forEach((d, i) => {
    const a = processed.actual[i];
    const p = processed.p50[i];
    if (a !== null) {
      historicalX.push(d);
      historicalY.push(a);
    } else if (p !== null) {
      forecastX.push(d);
      forecastY.push(p);
    }
  });

  if (historicalX.length > 0) {
    traces.push({
      type: 'scatter',
      mode: 'lines',
      name: 'Historical',
      x: historicalX,
      y: historicalY,
      line: { color: '#000000', width: 2.5 },
      hovertemplate: hoverTemplate({ name: 'Historical', format: '.1f', suffix: '%' }),
    });
  }

  // Splice the last historical point into the forecast so the lines connect.
  const finalForecastX = forecastX.slice();
  const finalForecastY = forecastY.slice();
  if (historicalX.length > 0 && forecastX.length > 0) {
    const lastHistDate = historicalX[historicalX.length - 1]!;
    const lastHistValue = historicalY[historicalY.length - 1]!;
    if (new Date(forecastX[0]!) >= new Date(lastHistDate)) {
      finalForecastX.unshift(lastHistDate);
      finalForecastY.unshift(lastHistValue);
    }
  }

  if (finalForecastX.length > 0) {
    traces.push({
      type: 'scatter',
      mode: 'lines',
      name: 'Forecast',
      x: finalForecastX,
      y: finalForecastY,
      line: { color: '#DC2626', width: 2.5 },
      hovertemplate: hoverTemplate({ name: 'Forecast', format: '.1f', suffix: '%' }),
    });
  }

  // Previous-month forecast (dashed gray).
  const prevX: string[] = [];
  const prevY: number[] = [];
  processed.dates.forEach((d, i) => {
    const v = oneMonth[i];
    if (v != null) {
      prevX.push(d);
      prevY.push(v);
    }
  });
  if (prevX.length > 0) {
    traces.push({
      type: 'scatter',
      mode: 'lines',
      name: 'One Month Ago Forecast',
      x: prevX,
      y: prevY,
      line: { color: '#6B7280', width: 2, dash: 'dash' },
    });
  }

  // Central-bank forecast markers (blue dots).
  const cbX: string[] = [];
  const cbY: number[] = [];
  processed.dates.forEach((d, i) => {
    const v = cb[i];
    if (v != null) {
      cbX.push(d);
      cbY.push(v);
    }
  });
  if (cbX.length > 0) {
    traces.push({
      type: 'scatter',
      mode: 'markers',
      name: 'Central Bank Forecast',
      x: cbX,
      y: cbY,
      marker: { color: '#2563EB', size: 6, symbol: 'circle' },
    });
  }

  return traces;
}

/**
 * Find the date where actual data ends and forecast begins (first date
 * where `actual` is null but `p50` has a value). Returns `null` when no
 * transition is found (e.g. all-forecast or all-actual data).
 */
function forecastStartDate(p: ProcessedFanData): string | null {
  for (let i = 0; i < p.dates.length; i++) {
    if (p.actual[i] === null && p.p50[i] !== null) return p.dates[i];
  }
  return null;
}

/**
 * Per-chart layout (title, y-range, tight margins).
 *
 * Layout decisions:
 *   - Title: 12px black, centered (x=0.5, xanchor='center'), y=0.98
 *   - Legend: horizontal, centered, 10px, above the plot area (y=1.0)
 *   - Tight margins (l:35, r:20, t:35, b:30)
 *   - Both axes: black border, mirrored, inside ticks (len=8)
 *   - Y-axis grid (#DCDCDC), X-axis grid (#DCDCDC) -- both on
 *   - Hover: x unified, date format "%b %Y"
 *   - fixedrange: true (no zoom/pan)
 *   - Font: 16px black
 *   - Dashed vertical line at the forecast start date
 */
export function buildFanLayout(
  def: FanChartDef,
  processed: ProcessedFanData,
  fixedYRange: YRange | null,
  cursorDate?: string | null,
  options?: { showCursorLine?: boolean },
): Partial<Layout> {
  const showCursorLine = options?.showCursorLine ?? true;
  const yRange = fixedYRange ?? computeYRange(processed);

  // Shared axis line + tick config: visible black border, mirrored, inside
  // ticks.
  const axisLineConfig = {
    showline: true,
    linecolor: '#000000',
    linewidth: 1,
    mirror: true,
    ticks: 'inside' as const,
    ticklen: 8,
    tickwidth: 1,
    tickcolor: '#000000',
  };

  // Dashed vertical line at the forecast start date. When a `cursorDate`
  // (selected vintage in YYYY-MM-DD form) is provided, also emit a
  // top-anchored annotation labelling the line.
  const shapes: Array<Partial<Shape>> = [];
  const annotations: Array<Partial<Annotations>> = [];
  // Prefer the explicit cursor date (selected vintage) over the
  // data-derived forecast-start. For variables like Dividend Yield where
  // the actual/forecast boundary in the underlying data is older than the
  // current vintage cursor, anchoring to `forecastStartDate` would draw
  // the line years out of date. Fall back to the data-derived value only
  // when no cursor is provided.
  const anchor = cursorDate ?? forecastStartDate(processed);
  if (anchor && showCursorLine) {
    shapes.push({
      type: 'line',
      xref: 'x',
      yref: 'paper',
      x0: anchor,
      x1: anchor,
      y0: 0,
      y1: 1,
      line: { color: 'rgba(0, 0, 0, 0.45)', width: 1.5, dash: 'dash' },
      layer: 'below',
    });
    annotations.push({
      xref: 'x',
      yref: 'paper',
      x: anchor,
      // Just inside the top edge of the plot: above it (yanchor: bottom)
      // the label lands in the legend row and the two texts collide.
      y: 1.0,
      yanchor: 'top',
      xanchor: 'center',
      text: anchor,
      showarrow: false,
      font: { size: 11, color: '#666' },
      bgcolor: 'rgba(255,255,255,0.8)',
      borderpad: 2,
    });
  }

  // Title (14px), horizontal 10px legend at y:1.0, and the tight fan-grid
  // margin come from the shared `chartLayout` fan family — one home for the
  // small-multiple layout language instead of hardcoding it here.
  const fanFrame = chartLayout({ family: 'fan', title: def.title });

  return {
    font: {
      family: "'Gill Sans MT', 'Gill Sans', 'Trebuchet MS', sans-serif",
      size: 16,
      color: '#000000',
    },
    title: fanFrame.title,
    showlegend: fanFrame.showlegend,
    legend: fanFrame.legend,
    margin: fanFrame.margin,
    shapes,
    annotations,
    yaxis: {
      range: [yRange.min, yRange.max],
      showgrid: true,
      gridcolor: '#DCDCDC',
      gridwidth: 1,
      tickformat: def.yFormat,
      fixedrange: true,
      ...axisLineConfig,
    },
    xaxis: {
      type: 'date',
      showgrid: true,
      gridcolor: '#DCDCDC',
      gridwidth: 1,
      hoverformat: '%b %Y',
      fixedrange: true,
      ...axisLineConfig,
    },
    hovermode: 'x unified',
    hoverlabel: {
      bgcolor: 'rgba(255,255,255,0.87)',
      bordercolor: '#666',
      align: 'left',
      font: { size: 12, color: 'black' },
    },
  };
}

/**
 * Y-axis range sized to hug the data, matching the legacy
 * `mars-fan-plots.js` `renderFanChart` axis logic:
 *
 *   dataMin = min(p16, p84, p50, actual, previous_month)
 *   dataMax = max(p16, p84, p50, actual, previous_month)
 *   yLimits = [floor(dataMin), ceil(dataMax)]
 *
 * Only the INNER band (p16/p84) feeds the axis — the outer p5/p95 cone is
 * deliberately excluded. The outer band fans out very wide into the forecast
 * horizon, so including it lets the extreme tails dominate the axis and the
 * actual data gets dwarfed (e.g. 10 Year Yield ballooning to -5..15 when the
 * series sits between ~2 and ~6). The outer cone extending past the axis edge
 * is acceptable — that is exactly what the legacy app renders.
 *
 * `floor`/`ceil` snaps the bounds to integer steps, matching the legacy
 * portal's tick layout (e.g. 10 Year Yield `3..5`, BAA Spread `0..3`).
 */
export function computeYRange(p: ProcessedFanData): YRange {
  const values: number[] = [];
  const push = (arr: Array<number | null>): void => {
    for (const v of arr) {
      if (v != null && Number.isFinite(v)) values.push(v);
    }
  };
  // Inner band + median + observed series only — never the p5/p95 outer cone.
  push(p.p16);
  push(p.p50);
  push(p.p84);
  push(p.actual);
  push(p.previous_month);
  if (!values.length) return { min: 0, max: 5 };
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const min = Math.floor(minValue);
  let max = Math.ceil(maxValue);
  // Guard against a flat integer series collapsing to a zero-height window
  // (e.g. every value exactly 3 → floor 3 / ceil 3): nudge the ceiling up so
  // the line still has visible breathing room.
  if (max <= min) max = min + 1;
  return { min, max };
}

/** Lookup the matching y-range from the global `y_ranges` endpoint. */
/**
 * Sanity bound for fan-chart y-ranges. The backend computes per-variable
 * ranges from the full historical vintage set, but a single rogue forecast
 * vintage (e.g. a corrupted Dividend Yield row at -554%) can blow the range
 * out by an order of magnitude and squash the actual chart to a flat line.
 *
 * Every variable rendered in this feature is a percent. The legitimate
 * historical range for 10 Year yields and BAA Spread tail percentiles can
 * span well beyond 100pp once forecast tails are included, so we use a much
 * looser bound that still catches the obviously corrupted -554% case but
 * passes through the genuine wide ranges for rates / spreads. Anything
 * beyond 500pp is treated as an artifact and we fall back to Plotly
 * auto-scaling on the visible vintage.
 */
/**
 * Per-variable sanity limits on the backend-supplied y-range. Anything larger
 * than the entry below (in percentage points) is treated as a corrupted /
 * wide-tail artefact and we fall back to Plotly auto-scaling on the visible
 * vintage.
 *
 * 10Y yields and BAA spreads have visibly tighter natural display windows
 * than GDP/inflation tails — using a single global 500pp limit was letting
 * pathological backend ranges flow through and mash those two charts flat.
 */
const Y_RANGE_LIMITS: Record<string, number> = {
  GDP: 100,
  Inflation: 100,
  'T-Bill': 50,
  'Dividend Yield': 30,
  '10 Year': 30,
  'BAA Spread': 20,
};
const DEFAULT_Y_RANGE_LIMIT = 100;

export function resolveYRange(
  ranges: Record<string, YRange> | undefined,
  def: FanChartDef,
): YRange | null {
  if (!ranges) return null;
  // The backend keys match the `variable` field for the macro variables.
  const r = ranges[def.variable];
  if (!r) return null;
  if (!Number.isFinite(r.min) || !Number.isFinite(r.max)) return null;
  const limit = Y_RANGE_LIMITS[def.variable] ?? DEFAULT_Y_RANGE_LIMIT;
  if (r.max - r.min > limit) return null;
  return r;
}
